import React, { useState } from 'react';
import useContact from '../../../hooks/useContact';
import './Contact.css'; 
import Header from '../../../components/common/Header/header';
import ErrorDisplay from '../../../components/common/Error/Error';
import Toast from '../../../components/common/Toast/Toast';
import { Link } from "react-router-dom";

import ReCAPTCHA from "react-google-recaptcha";

function Contact() {

    // Form state, including honeypot field 'website'
    const [form, setForm] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
        website: '', // honeypot field for bot detection
    });
    // Status message for user feedback
    const [status, setStatus] = useState('');
    const [error, setError] = useState(null);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    // Captcha value state
    const [captchaValue, setCaptchaValue] = useState(null);
    const { loading, error: hookError, submitContact } = useContact();

    // Handle input changes for all form fields
    const handleChange = e => {
        if (status) {
            setStatus('');
        }
        if (error) {
            setError(null);
        }
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    // Handle form submission
    const handleSubmit = async e => {
        e.preventDefault();
        // Basic client-side validation
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            setStatus('Veuillez remplir tous les champs obligatoires.');
            return;
        }
        // Check if captcha is completed
        if (!captchaValue) {
            setStatus('Veuillez compléter le captcha.');
            return;
        }
        setError(null);
        setStatus('Envoi...');
        try {
            const now = new Date();
            // Honeypot: if website field is filled, likely a bot
            const IsBot = !!(form.website && form.website.trim() !== '');
            // Send form data to backend
            const payload = {
                Name: form.name,
                Phone: form.phone,
                Email: form.email,
                Message: form.message,
                DateWrite: now,
                DateSend: now,
                IsBot,
                CaptchaToken: captchaValue
            };
            const response = await submitContact(payload);
            if (response) {
                // Show success toast instead of status
                setToastMessage('Message envoyé avec succès !');
                setShowToast(true);
                setStatus(''); // Clear status
                // Reset form fields
                setForm({ name: '', email: '', phone: '', message: '', website: '' });
                setCaptchaValue(null); // Reset captcha
            }
        } catch {
            setStatus('');
        }
    };

    return (
        <div className="contact-container">
            {showToast && (
                <Toast 
                    message={toastMessage}
                    type="success"
                    onClose={() => setShowToast(false)}
                />
            )}
            <div className='contact-header'>
                <Header />
            </div>
            <div className='contact-content'>
                <form className='contact-form' onSubmit={handleSubmit}>
                    <h1>Contactez nous</h1>
                    <p>Si vous avez des questions ou si vous souhaitez obtenir de plus amples informations, n'hésitez pas à me contacter.</p>
                    {/* Show error if present */}
                    {error && <ErrorDisplay errorData={error} />}
                    {hookError && <ErrorDisplay errorData={hookError} />}
                    {/* Contact form fields */}
                    <table className="contact-table">
                        <tbody>
                            {/* Name field */}
                            <tr>
                                <td className='contact-table-label'>Nom :</td>
                                <td>
                                    <input 
                                        type="text" 
                                        id="name"
                                        name="name"
                                        placeholder="Entrez votre nom" 
                                        required 
                                        aria-required="true"
                                        value={form.name}
                                        onChange={handleChange}
                                    />
                                </td>
                            </tr>
                            {/* Email field */}
                            <tr>
                                <td className='contact-table-label'>Email :</td>
                                <td>
                                    <input 
                                        type="email" 
                                        id="email"
                                        name="email"
                                        placeholder="Entrez votre email" 
                                        required 
                                        aria-required="true"
                                        value={form.email}
                                        onChange={handleChange}
                                    />  
                                </td>
                            </tr>
                            {/* Phone field */}
                            <tr>
                                <td className='contact-table-label'>Numéro de téléphone :</td>
                                <td>
                                    <input 
                                        type="tel" 
                                        id="phone"
                                        name="phone"
                                        placeholder="Entrez votre numéro de téléphone" 
                                        pattern="[0-9+\-\(\) ]{7,}"
                                        value={form.phone}
                                        onChange={handleChange}
                                    />
                                </td>
                            </tr>
                            {/* Message field */}
                            <tr>
                                <td className='contact-table-label'>Message :</td>
                                <td>
                                    <textarea 
                                        id="message"
                                        name="message"
                                        placeholder="Entrez votre message"
                                        required
                                        aria-required="true"
                                        value={form.message}
                                        onChange={handleChange}
                                    />
                                </td> 
                            </tr>
                            {/* Captcha field */}
                            <tr>
                                <td className='contact-table-label'>Captcha :</td>
                                <td>
                                    <div className="captcha-container">
                                        <ReCAPTCHA
                                            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                            onChange={(value) => {
                                                setCaptchaValue(value);
                                                if (value) {
                                                    setStatus('');
                                                    setError(null);
                                                }
                                            }}
                                            onErrored={() => setError('Le chargement de Captcha a échoué. Veuillez actualiser et réessayer.')}
                                            onExpired={() => setError('Captcha a expiré. Veuillez réessayer.')}
                                        />
                                    </div>
                                </td>
                            </tr>
                            {/* Submit button */}
                            <tr>
                                <td colSpan="2">
                                    <button type="submit" disabled={status === 'Envoi...' || loading}>
                                        {loading ? 'Envoi...' : 'Envoyer un message'}
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    {/* Honeypot field (hidden from users, visible to bots) */}
                    <input
                        type="text"
                        name="website"
                        value={form.website || ''}
                        onChange={handleChange}
                        className="contact-hidden"
                        autoComplete="off"
                        tabIndex={-1}
                    />
                    {/* Status message for user feedback */}
                    {status && (
                        <p className="contact-status" role="status" aria-live="polite">
                            {status}
                        </p>
                    )}
                </form>

            <footer>
                <Link to="/legal-mention">Mentions légales</Link>
                
            </footer>
            </div>
        </div>
    );
}

export default Contact;